import { NavLink, Outlet, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Loading, StatusBadge } from "@/components/Kpi";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { to: "", label: "Dashboard", end: true }, { to: "anagrafica", label: "Anagrafica" }, { to: "conto-economico", label: "Conto economico" },
  { to: "movimenti", label: "Movimenti" }, { to: "unita", label: "Unità" }, { to: "finanziamenti", label: "Finanziamenti" }, { to: "cash-flow", label: "Cash flow" },
  { to: "scenari", label: "Scenari" }, { to: "imposte", label: "Imposte" }, { to: "controlli", label: "Controlli" }, { to: "documenti", label: "Documenti e attività" }, { to: "report", label: "Report" },
];
const INVESTOR_TABS = ["", "unita", "documenti", "report", "finanziamenti", "cash-flow"];

function DraftNotice({ versions, id, hide }) {
  const prev = versions.filter((v) => ["preventivo", "revisione"].includes(v.type));
  const approved = prev.filter((v) => ["approvata", "chiusa"].includes(v.status));
  const draft = [...prev].reverse().find((v) => ["bozza", "riaperta"].includes(v.status));
  if (hide || !draft || !approved.length) return null;
  return <div className="mb-4 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded px-3 py-2" data-testid="draft-revision-notice">
    Esiste una <b>{draft.type === "revisione" ? "Revisione" : "Preventivo"} {draft.number} in bozza</b> con le ultime modifiche del Conto economico. Dashboard, imposte, cash flow, report e tutte le altre sezioni usano il Preventivo <b>approvato</b> ({approved[approved.length - 1].type === "revisione" ? "Revisione" : "Preventivo"} {approved[approved.length - 1].number}) finché la bozza non viene approvata.
    <NavLink to={`/operazioni/${id}/conto-economico`} className="ml-2 underline" data-testid="draft-revision-link">Vai al Conto economico per approvare</NavLink>
  </div>;
}

export default function ProjectLayout() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data: project, isLoading, error } = useQuery({ queryKey: ["project", id], queryFn: () => api.get(`/projects/${id}`).then((r) => r.data) });
  const { data: versions = [] } = useQuery({ queryKey: ["versions", id], queryFn: () => api.get(`/projects/${id}/versions`).then((r) => r.data), enabled: !!project });
  const loc = useLocation();
  if (isLoading) return <Loading />;
  if (error) return <div className="text-red-800" data-testid="project-error">Operazione non accessibile: {error.response?.data?.detail || error.message}</div>;
  const all = project?.type === "co_investimento" ? [...TABS.slice(0, 7), { to: "co-investimento", label: "Co-investimento" }, { to: "brochure", label: "Brochure" }, ...TABS.slice(7)] : TABS;
  const tabs = user.role === "investitore" ? all.filter((t) => INVESTOR_TABS.includes(t.to)) : all;
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <h1 className="font-heading text-2xl font-semibold" data-testid="project-title">{project.code} · {project.name}</h1>
        <StatusBadge s={project.status} />
        <span className="text-sm text-slate-500">{project.company_name} · {project.city}</span>
      </div>
      <nav className="flex gap-1 overflow-x-auto border-b mb-6" data-testid="project-tabs">
        {tabs.map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `tab-link ${isActive ? "active" : ""}`} data-testid={`tab-${t.to || "dashboard"}`}>{t.label}</NavLink>
        ))}
      </nav>
      <DraftNotice versions={versions} id={id} hide={loc.pathname.endsWith("/conto-economico")} />
      <Outlet context={{ project }} />
    </div>
  );
}
